// consentimientosService.js - Servicios para manejar consentimientos informados
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  getFirestore
} from 'firebase/firestore';
import app from '../config/firebase';

// Inicializar Firestore directamente aquí
const db = getFirestore(app);

// CONSENTIMIENTOS INFORMADOS (Informed Consents) Services
export const consentimientosService = {
  // Crear nuevo consentimiento
  async createConsentimiento(consentimientoData) {
    try {
      console.log('🔏 CONSENTIMIENTO SERVICE: Creando nuevo consentimiento', consentimientoData);
      
      // Verificar que el ID del paciente esté presente
      if (!consentimientoData.pacienteId) {
        console.error('❌ CONSENTIMIENTO SERVICE: Error - No se proporcionó pacienteId');
        return { success: false, error: 'No se proporcionó ID de paciente' };
      }
      
      // Asegurarse de que activo sea true (podría estar faltando)
      const docData = {
        ...consentimientoData,
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp(),
        activo: true
      };
      
      console.log('📄 CONSENTIMIENTO SERVICE: Datos a guardar:', docData);
      
      const docRef = await addDoc(collection(db, 'consentimientos'), docData);
      console.log('✅ CONSENTIMIENTO SERVICE: Consentimiento creado correctamente con ID:', docRef.id);
      
      return { id: docRef.id, success: true };
    } catch (error) {
      console.error('❌ CONSENTIMIENTO SERVICE: Error creando consentimiento:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener consentimientos por paciente
  async getConsentimientosByPatient(pacienteId) {
    try {
      console.log('🔍 CONSENTIMIENTO SERVICE: Buscando consentimientos para paciente', pacienteId);
      
      // Más debugging para entender los problemas
      if (!pacienteId) {
        console.error('❌ CONSENTIMIENTO SERVICE: Error - ID de paciente inválido:', pacienteId);
        return { success: false, error: 'ID de paciente inválido' };
      }
      
      console.log('🔍 CONSENTIMIENTO SERVICE: Verificando acceso a la colección de consentimientos');
      
      try {
        // Primero intentamos una consulta simple para ver si podemos acceder a la colección
        const simpleQuery = await getDocs(collection(db, 'consentimientos'));
        console.log(`✅ CONSENTIMIENTO SERVICE: Acceso correcto, ${simpleQuery.size} documentos en la colección`);
      } catch (accessError) {
        console.error('❌ CONSENTIMIENTO SERVICE: Error accediendo a la colección:', accessError);
        return { success: false, error: 'Error de acceso a la colección: ' + accessError.message };
      }
      
      console.log('🔍 CONSENTIMIENTO SERVICE: Construyendo query con filtros');
      const q = query(
        collection(db, 'consentimientos'),
        where('pacienteId', '==', pacienteId),
        where('activo', '==', true),
        orderBy('fechaCreacion', 'desc')
      );
      
      console.log('🔍 CONSENTIMIENTO SERVICE: Ejecutando query');
      const querySnapshot = await getDocs(q);
      console.log('✅ CONSENTIMIENTO SERVICE: Query ejecutada, documentos encontrados:', querySnapshot.size);
      
      const consentimientos = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('📄 CONSENTIMIENTO SERVICE: Documento procesado:', doc.id, data);
        consentimientos.push({
          id: doc.id,
          ...data,
          fecha: data.fechaCreacion?.toDate?.()?.toLocaleDateString() || new Date().toLocaleDateString(),
          fechaCompleta: data.fechaCreacion?.toDate?.() || new Date()
        });
      });
      
      console.log('✅ CONSENTIMIENTO SERVICE: Consentimientos encontrados:', consentimientos.length);
      if (consentimientos.length === 0) {
        console.log('ℹ️ CONSENTIMIENTO SERVICE: No se encontraron consentimientos para el paciente:', pacienteId);
      }
      
      return { success: true, data: consentimientos };
    } catch (error) {
      console.error('❌ CONSENTIMIENTO SERVICE: Error obteniendo consentimientos:', error);
      return { success: false, error: error.message };
    }
  },

  // Actualizar consentimiento
  async updateConsentimiento(consentimientoId, updatedData) {
    try {
      console.log('✏️ CONSENTIMIENTO SERVICE: Actualizando consentimiento', consentimientoId);
      const consentimientoRef = doc(db, 'consentimientos', consentimientoId);
      await updateDoc(consentimientoRef, {
        ...updatedData,
        fechaActualizacion: serverTimestamp()
      });
      console.log('✅ CONSENTIMIENTO SERVICE: Consentimiento actualizado correctamente');
      return { success: true };
    } catch (error) {
      console.error('❌ CONSENTIMIENTO SERVICE: Error actualizando consentimiento:', error);
      return { success: false, error: error.message };
    }
  },

  // Eliminar consentimiento (soft delete)
  async deleteConsentimiento(consentimientoId) {
    try {
      console.log('🗑️ CONSENTIMIENTO SERVICE: Eliminando consentimiento', consentimientoId);
      const consentimientoRef = doc(db, 'consentimientos', consentimientoId);
      await updateDoc(consentimientoRef, {
        activo: false,
        fechaEliminacion: serverTimestamp()
      });
      console.log('✅ CONSENTIMIENTO SERVICE: Consentimiento eliminado correctamente');
      return { success: true };
    } catch (error) {
      console.error('❌ CONSENTIMIENTO SERVICE: Error eliminando consentimiento:', error);
      return { success: false, error: error.message };
    }
  }
};

export default consentimientosService;
